import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';
import {
  Monitor, ArrowLeft, Play, Film, FileVideo, Image, Clock, Copy, DollarSign, Zap,
  Radio, ChevronDown, ChevronUp, Volume2, SkipForward, ArrowLeftCircle, Layers, Timer, GripVertical, X,
  Gamepad2, RefreshCw, AlarmClock, Ban, Sparkles,
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import axios from 'axios';

function formatDuration(s) { if (!s) return '5s'; const m = Math.floor(s/60); const sec = Math.floor(s%60); return m > 0 ? `${m}:${sec.toString().padStart(2,'0')}` : `${sec}s`; }
function formatTime(s) { const h=Math.floor(s/3600); const m=Math.floor((s%3600)/60); const sec=s%60; return `${h.toString().padStart(2,'0')}:${m.toString().padStart(2,'0')}:${sec.toString().padStart(2,'0')}`; }

export default function StreamerConsole() {
  const { token, API, user } = useAuth();
  const navigate = useNavigate();
  const [approvedAds, setApprovedAds] = useState([]);
  const [wsConnected, setWsConnected] = useState(false);
  const [sessionStart] = useState(Date.now());
  const [sessionTime, setSessionTime] = useState(0);
  const [sessionTriggers, setSessionTriggers] = useState(0);
  const [sessionEarnings, setSessionEarnings] = useState(0);
  const [showObs, setShowObs] = useState(false);
  // Ad Break State
  const [activeBreak, setActiveBreak] = useState(null);
  const [breakStatus, setBreakStatus] = useState(null); // null, 'playing', 'holding', 'ending'
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [countdown, setCountdown] = useState(null);
  // Overlay state
  const [overlayAds, setOverlayAds] = useState([]);
  const [overlayInterval, setOverlayInterval] = useState(0); // 0=manual, 5/10/20/30/60 min
  const [overlayDuration, setOverlayDuration] = useState(10); // display seconds
  const [overlayActive, setOverlayActive] = useState(false);
  const [selectedOverlay, setSelectedOverlay] = useState(null);
  const [playedAdIds, setPlayedAdIds] = useState(new Set());
  const [dragIdx, setDragIdx] = useState(null);
  const [hasHoldingScreen, setHasHoldingScreen] = useState(false);
  // Break scheduler + hotkeys + overlay styling
  const [cadence, setCadence] = useState(0);
  const [autoFire, setAutoFire] = useState(false);
  const [holdingCountdownSec, setHoldingCountdownSec] = useState(60);
  const [transitionStyle, setTransitionStyle] = useState('fade');
  const [overlayPosition, setOverlayPosition] = useState('bottom-right');
  const [lastBreakAt, setLastBreakAt] = useState(Date.now());
  const [nextIn, setNextIn] = useState(null);
  const [hotkeys, setHotkeys] = useState(null);
  const nudgedRef = useRef(false);
  const autoFiringRef = useRef(false);
  const prevOnAirRef = useRef(false);
  const canCutToCommercial = approvedAds.length > 0 || hasHoldingScreen;
  const isOnAir = breakStatus === 'playing' || breakStatus === 'holding' || breakStatus === 'ending';
  const overlayTimerRef = useRef(null);
  const wsRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { const t = setInterval(() => setSessionTime(Math.floor((Date.now()-sessionStart)/1000)), 1000); return () => clearInterval(t); }, [sessionStart]);

  const fetchAds = useCallback(async () => {
    try {
      const [plRes] = await Promise.all([
        axios.get(`${API}/placements/streamer-queue`, { headers }).catch(() => ({ data: [] })),
      ]);
      // Only show placement-based ads with remaining slots — no general approved ads
      const pAds = plRes.data.map(p => ({ ...p, id: p.adId, url: p.adUrl, mimeType: p.adMimeType, filename: p.adFilename, isPlacement: true, placementId: p.id }));
      setApprovedAds(pAds);
    } catch (e) { console.error(e); }
    // Check for holding screen (own or brand-sponsored)
    try {
      const profRes = await axios.get(`${API}/streamer-profiles/me`, { headers });
      const hasOwn = !!(profRes.data?.holdingImageUrl && profRes.data.holdingImageUrl.length > 1);
      setHasHoldingScreen(hasOwn);
      const p = profRes.data || {};
      setCadence(p.breakCadenceMinutes || 0);
      setAutoFire(!!p.autoFireBreaks);
      setHoldingCountdownSec(p.holdingCountdownSeconds ?? 60);
      setTransitionStyle(p.transitionStyle || 'fade');
      setOverlayPosition(p.overlayPosition || 'bottom-right');
      // Also check for brand-sponsored holding placement
      if (!hasOwn) {
        const incomingRes = await axios.get(`${API}/placements/incoming`, { headers }).catch(() => ({ data: [] }));
        const hasSponsored = incomingRes.data.some(p => p.adType === 'holding' && p.status === 'approved' && (p.slotsRemaining || 0) > 0);
        setHasHoldingScreen(hasSponsored);
      }
    } catch (e) {}
    // Fetch overlay ads
    try {
      const oRes = await axios.get(`${API}/ads/approved-overlays`, { headers });
      setOverlayAds(oRes.data);
      if (oRes.data.length > 0 && !selectedOverlay) setSelectedOverlay(oRes.data[0]);
    } catch (e) {}
  }, [API, token]);

  useEffect(() => { fetchAds(); }, [fetchAds]);

  // Check for existing active break on load
  useEffect(() => {
    const checkBreak = async () => {
      try {
        const res = await axios.get(`${API}/ads/current-break`);
        if (res.data && res.data.streamerId === user?.id && ['playing', 'holding'].includes(res.data.status)) {
          setActiveBreak(res.data);
          setBreakStatus(res.data.status);
          setCurrentAdIndex(res.data.currentAdIndex || 0);
        } else {
          // No active break — make sure console is reset
          setActiveBreak(null);
          setBreakStatus(null);
        }
      } catch (e) {}
    };
    checkBreak();
  }, [API, user]);

  // Periodic check: if we think a break is active, verify it still is
  useEffect(() => {
    if (!breakStatus) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/ads/current-break`);
        if (!res.data || res.data.status === 'ended' || res.data.status === 'ending') {
          // Break is over — reset console
          if (res.data?.status === 'ending') {
            // Still ending, wait
          } else {
            setActiveBreak(null);
            setBreakStatus(null);
            fetchAds();
          }
        }
      } catch {}
    }, 2000);
    return () => clearInterval(interval);
  }, [breakStatus, API, fetchAds]);

  // WebSocket
  useEffect(() => {
    const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace('https://','wss://').replace('http://','ws://');
    if (!wsUrl) return;
    const connect = () => {
      const ws = new WebSocket(`${wsUrl}/api/ws/triggers`);
      ws.onopen = () => setWsConnected(true);
      ws.onmessage = (e) => {
        try {
          const d = JSON.parse(e.data);
          if (d.type === 'ad_break_start') { setActiveBreak(prev => ({...prev, ...d})); setBreakStatus('playing'); setCurrentAdIndex(0); }
          if (d.type === 'ad_break_next') { setCurrentAdIndex(d.currentAdIndex); }
          if (d.type === 'ad_break_holding') { setBreakStatus('holding'); }
          if (d.type === 'back_to_stream') { if (!d.finishCurrent) { setActiveBreak(null); setBreakStatus(null); fetchAds(); } else { setBreakStatus('ending'); } }
          if (d.type === 'break_ended') { setActiveBreak(null); setBreakStatus(null); fetchAds(); }
        } catch {}
      };
      ws.onclose = () => { setWsConnected(false); setTimeout(connect, 3000); };
      ws.onerror = () => setWsConnected(false);
      wsRef.current = ws;
    };
    connect();
    return () => wsRef.current?.close();
  }, []);

  const startBreak = async (withCountdown = false) => {
    axios.post(`${API}/ads/prewarm`, {}, { headers }).catch(() => {});
    if (withCountdown) {
      for (let i = 3; i >= 1; i--) { setCountdown(i); await new Promise(r => setTimeout(r, 1000)); }
      setCountdown(null);
    }
    try {
      const res = await axios.post(`${API}/ads/start-break`, {}, { headers });
      setActiveBreak(res.data);
      if (res.data.status === 'holding' || res.data.totalAds === 0) {
        // No video ads — went straight to holding screen
        setBreakStatus('holding');
        toast.success('Holding screen active.');
      } else {
        setBreakStatus('playing');
        setCurrentAdIndex(0);
        setSessionTriggers(p => p + res.data.totalAds);
        const breakAdIds = new Set((res.data.ads || []).map(a => a.adId));
        setPlayedAdIds(prev => new Set([...prev, ...breakAdIds]));
        toast.success(`Ad break started! ${res.data.totalAds} ads queued.`);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to start break'); }
  };

  const backToStream = async () => {
    try {
      const res = await axios.post(`${API}/ads/back-to-stream`, {}, { headers });
      if (res.data.finishCurrent) {
        toast.success('Current ad will finish, then back to stream.');
        setBreakStatus('ending');
        // Don't force-end — let the OBS overlay finish the ad naturally
        // The periodic polling (every 2s) will detect when the break is fully ended and reset the console
      } else {
        toast.success('Back to stream!');
        setActiveBreak(null);
        setBreakStatus(null);
        fetchAds();
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  // ---- Keep the overlay's video cache warm so a break never opens on a blank screen ----
  useEffect(() => {
    const warm = () => axios.post(`${API}/ads/prewarm`, {}, { headers }).catch(() => {});
    warm();
    const t = setInterval(warm, 45000);
    return () => clearInterval(t);
  }, [API, token]);

  // ---- Console settings persistence ----
  const saveConsoleSettings = useCallback(async (patch) => {
    try { await axios.put(`${API}/streamer/console-settings`, patch, { headers }); } catch { toast.error('Could not save setting'); }
  }, [API, token]);

  // ---- Stream Deck / hotkey URLs ----
  useEffect(() => {
    axios.get(`${API}/streamer/trigger-key`, { headers })
      .then(r => setHotkeys(r.data)).catch(() => {});
  }, [API, token]);

  const rotateKey = async () => {
    try {
      const r = await axios.post(`${API}/streamer/trigger-key/rotate`, {}, { headers });
      setHotkeys(r.data);
      toast.success('New trigger key generated. Update your Stream Deck buttons.');
    } catch { toast.error('Failed to rotate key'); }
  };

  // ---- Break scheduler ----
  useEffect(() => {
    if (prevOnAirRef.current !== isOnAir) {
      prevOnAirRef.current = isOnAir;
      setLastBreakAt(Date.now());
      nudgedRef.current = false;
    }
  }, [isOnAir]);

  useEffect(() => {
    if (!cadence) { setNextIn(null); return; }
    const tick = () => setNextIn(cadence * 60 - Math.floor((Date.now() - lastBreakAt) / 1000));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [cadence, lastBreakAt]);

  useEffect(() => {
    if (nextIn === null || isOnAir) return;
    if (nextIn <= 60 && nextIn > 0 && !nudgedRef.current) {
      nudgedRef.current = true;
      toast.warning(`Ad break due in ${nextIn}s`, { description: autoFire ? 'It will fire automatically.' : 'Hit Cut to Commercial when you\'re ready.' });
    }
    if (nextIn <= 0 && autoFire && canCutToCommercial && !autoFiringRef.current) {
      autoFiringRef.current = true;
      startBreak(true).finally(() => { autoFiringRef.current = false; });
    }
  }, [nextIn, autoFire, isOnAir]);

  const snooze = (mins) => { setLastBreakAt(v => v + mins * 60 * 1000); nudgedRef.current = false; toast.success(`Snoozed ${mins} min`); };

  // Overlay trigger
  const triggerOverlay = async (ad = null) => {
    const target = ad || selectedOverlay;
    if (!target) { toast.error('No overlay ad selected'); return; }
    setOverlayActive(true);
    try {
      const res = await axios.post(`${API}/overlay/trigger`, { adId: target.id, position: overlayPosition }, { headers });
      toast.success(`Overlay triggered! ${res.data.earnings > 0 ? `+$${res.data.earnings.toFixed(4)}` : ''}`);
      setSessionEarnings(p => p + (res.data.earnings || 0));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setTimeout(() => setOverlayActive(false), (overlayDuration + 1) * 1000);
  };

  // Auto overlay timer
  useEffect(() => {
    if (overlayTimerRef.current) clearInterval(overlayTimerRef.current);
    if (overlayInterval > 0 && overlayAds.length > 0) {
      overlayTimerRef.current = setInterval(() => {
        // Pick a random overlay ad
        const randomAd = overlayAds[Math.floor(Math.random() * overlayAds.length)];
        triggerOverlay(randomAd);
      }, overlayInterval * 60 * 1000);
    }
    return () => { if (overlayTimerRef.current) clearInterval(overlayTimerRef.current); };
  }, [overlayInterval, overlayAds]);

  // Save overlay config
  const saveOverlayConfig = async (interval, duration) => {
    try {
      await axios.put(`${API}/overlay/config?overlayInterval=${interval}&overlayDisplayDuration=${duration}`, {}, { headers });
    } catch {}
  };

  const getFullUrl = (url) => url?.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;

  // Drag-and-drop reorder
  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => { e.preventDefault(); };

  const removeFromQueue = async (placementId) => {
    try {
      await axios.post(`${API}/placements/${placementId}/remove-from-queue`, {}, { headers });
      toast.success('Ad removed from queue');
      fetchAds();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const handleDrop = async (e, dropIdx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === dropIdx) return;
    const upcoming = approvedAds.filter(a => !playedAdIds.has(a.id));
    const played = approvedAds.filter(a => playedAdIds.has(a.id));
    const reordered = [...upcoming];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(dropIdx, 0, moved);
    setApprovedAds([...reordered, ...played]);
    setDragIdx(null);
    // Save order to backend
    try {
      await axios.put(`${API}/placements/reorder-queue`, { orderedPlacementIds: reordered.map(a => a.placementId || a.id) }, { headers });
    } catch {}
  };
  const obsUrl = hotkeys?.triggerKey ? `${window.location.origin}/obs-overlay?key=${hotkeys.triggerKey}` : `${window.location.origin}/obs-overlay`;
  const currentAd = activeBreak?.ads?.[currentAdIndex];

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-[#050505] flex flex-col">

        {/* TOP STRIP */}
        <header className="h-12 border-b border-white/[0.06] bg-[#0a0a0a] flex items-center px-4 gap-4 flex-shrink-0">
          <Button data-testid="back-to-home" variant="ghost" onClick={() => navigate('/')} className="text-gray-500 hover:text-white hover:bg-white/5 h-8 w-8 p-0"><ArrowLeft className="w-4 h-4" /></Button>
          <div className="flex items-center gap-2"><Radio className="w-3.5 h-3.5 text-[#FF3B30]" /><span className="font-heading text-sm font-bold text-white uppercase tracking-wider">Stream Console</span></div>
          <div className="h-4 w-px bg-white/10" />
          <div data-testid="ws-connection-status" className="flex items-center gap-1.5">
            {wsConnected ? <><div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" /><span className="text-[10px] font-mono text-emerald-400 uppercase">Live</span></> :
              <><div className="w-1.5 h-1.5 bg-red-400 rounded-full" /><span className="text-[10px] font-mono text-red-400 uppercase">Offline</span></>}
          </div>
          <div className="h-4 w-px bg-white/10" />
          <div className="flex items-center gap-1.5"><Clock className="w-3 h-3 text-gray-600" /><span className="text-xs font-mono text-gray-400">{formatTime(sessionTime)}</span></div>
          {cadence > 0 && (
            <>
              <div className="h-4 w-px bg-white/10" />
              <div data-testid="next-break-chip" className={`flex items-center gap-1.5 px-2 py-0.5 rounded border ${nextIn !== null && nextIn <= 60 ? 'border-[#FF3B30]/40 bg-[#FF3B30]/10' : 'border-white/10 bg-white/[0.02]'}`}>
                <AlarmClock className={`w-3 h-3 ${nextIn !== null && nextIn <= 60 ? 'text-[#FF3B30]' : 'text-gray-500'}`} />
                <span className={`text-xs font-mono ${nextIn !== null && nextIn <= 60 ? 'text-[#FF3B30]' : 'text-gray-400'}`}>
                  {nextIn === null ? '--:--' : nextIn > 0 ? formatTime(nextIn).slice(3) : 'DUE'}
                </span>
                {autoFire && <span className="text-[9px] font-mono text-emerald-400 uppercase">auto</span>}
              </div>
            </>
          )}
          <div className="flex-1" />
          <div className="flex items-center gap-5">
            <div className="flex items-center gap-1.5"><Zap className="w-3 h-3 text-[#FF3B30]" /><span className="text-xs font-mono text-white">{sessionTriggers}</span><span className="text-[10px] font-mono text-gray-600 uppercase">ads</span></div>
            <div className="flex items-center gap-1.5"><DollarSign className="w-3 h-3 text-emerald-400" /><span className="text-xs font-mono text-emerald-400">${sessionEarnings.toFixed(4)}</span><span className="text-[10px] font-mono text-gray-600 uppercase">session</span></div>
          </div>
          <div className="h-4 w-px bg-white/10" />
          <button onClick={() => setShowObs(!showObs)} className="text-[10px] font-mono text-gray-500 hover:text-white uppercase tracking-wider flex items-center gap-1">
            <Monitor className="w-3 h-3" /> OBS {showObs ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        </header>

        {showObs && <div className="border-b border-white/[0.06] bg-[#0a0a0a] px-4 py-3 space-y-3">
          <div className="flex items-center gap-4">
            <span className="text-[10px] font-mono text-gray-500 uppercase tracking-widest w-28">Browser Source:</span>
            <code className="bg-black/40 border border-white/10 rounded px-3 py-1.5 text-xs font-mono text-emerald-400 select-all">{obsUrl}</code>
            <Button size="sm" variant="ghost" className="text-gray-500 hover:text-white h-7 px-2 border border-white/10" onClick={() => { navigator.clipboard.writeText(obsUrl); toast.success('Copied!'); }}><Copy className="w-3 h-3 mr-1" /> Copy</Button>
            <span className="text-[10px] text-gray-600 font-mono">1920x1080</span>
          </div>
          {hotkeys && (
            <>
              <div className="flex items-center gap-2 pt-1">
                <Gamepad2 className="w-3.5 h-3.5 text-[#FF3B30]" />
                <span className="text-[10px] font-mono text-gray-400 uppercase tracking-widest">Stream Deck / Hotkey URLs</span>
                <span className="text-[10px] font-mono text-gray-600">— bind these to a button, no alt-tab needed</span>
                <button data-testid="rotate-trigger-key" onClick={rotateKey} className="ml-auto text-[10px] font-mono text-gray-500 hover:text-[#FF3B30] uppercase flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Rotate Key</button>
              </div>
              {[['Cut to Commercial', hotkeys.cutPath, 'cut'], ['Back to Stream', hotkeys.backPath, 'back']].map(([label, path, id]) => {
                const full = `${process.env.REACT_APP_BACKEND_URL}${path}`;
                return (
                  <div key={id} className="flex items-center gap-4">
                    <span className="text-[10px] font-mono text-gray-500 uppercase tracking-widest w-28">{label}:</span>
                    <code data-testid={`hotkey-url-${id}`} className="bg-black/40 border border-white/10 rounded px-3 py-1.5 text-[11px] font-mono text-[#FF3B30]/90 select-all truncate max-w-xl">{full}</code>
                    <Button size="sm" variant="ghost" data-testid={`copy-hotkey-${id}`} className="text-gray-500 hover:text-white h-7 px-2 border border-white/10" onClick={() => { navigator.clipboard.writeText(full); toast.success(`${label} URL copied`); }}><Copy className="w-3 h-3 mr-1" /> Copy</Button>
                  </div>
                );
              })}
              <p className="text-[10px] text-gray-600 font-mono">Stream Deck → System → Website → paste URL → enable "Access in background". Keep these secret.</p>
            </>
          )}
        </div>}

        {/* MAIN DECK */}
        <div className="flex-1 flex overflow-hidden">

          {/* LEFT: Ad Queue */}
          <div className="w-72 border-r border-white/[0.06] bg-[#0a0a0a] flex flex-col flex-shrink-0">
            <div className="px-4 py-3 border-b border-white/[0.06] flex items-center justify-between">
              <span className="text-xs font-mono text-gray-500 uppercase tracking-widest">Ad Queue</span>
              <div className="flex items-center gap-2">
                <Badge className="bg-white/5 text-gray-400 border-white/10 font-mono text-[10px]">
                  {approvedAds.filter(a => !playedAdIds.has(a.id)).length} upcoming
                </Badge>
                {playedAdIds.size > 0 && <Badge className="bg-emerald-500/5 text-emerald-500/50 border-emerald-500/10 font-mono text-[10px]">{playedAdIds.size} played</Badge>}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {approvedAds.length === 0 ? (
                <div className="text-center py-12 px-4"><Film className="w-8 h-8 mx-auto mb-2 text-gray-700" /><p className="text-xs text-gray-600">No ads in queue</p></div>
              ) : (
                <div className="py-1">
                  {/* Upcoming ads first, then played */}
                  {(() => {
                    const upcoming = approvedAds.filter(a => !playedAdIds.has(a.id));
                    const played = approvedAds.filter(a => playedAdIds.has(a.id));
                    return [...upcoming, ...played].map((ad, idx) => {
                    const isVideo = ad.mimeType?.startsWith('video/');
                    const isPlayingInBreak = isOnAir && activeBreak?.ads?.[currentAdIndex]?.adId === ad.id;
                    const isPlayed = playedAdIds.has(ad.id);
                    const isUpcoming = !isPlayed;
                    const upcomingIdx = isUpcoming ? upcoming.indexOf(ad) : -1;
                    return (
                      <div key={`${ad.id}-${idx}`} data-testid={`queue-ad-${ad.id}`}
                        draggable={isUpcoming && !isOnAir}
                        onDragStart={() => isUpcoming && handleDragStart(upcomingIdx)}
                        onDragOver={(e) => isUpcoming && handleDragOver(e, upcomingIdx)}
                        onDrop={(e) => isUpcoming && handleDrop(e, upcomingIdx)}
                        className={`flex items-start gap-2 px-3 py-3 border-l-2 transition-all ${
                          isPlayingInBreak ? 'bg-[#FF3B30]/5 border-[#FF3B30]' :
                          dragIdx === upcomingIdx ? 'bg-[#7C3AED]/5 border-[#7C3AED] opacity-50' :
                          isPlayed ? 'border-transparent opacity-35' :
                          'border-transparent hover:bg-white/[0.02] cursor-grab active:cursor-grabbing group'
                        }`}
                      >
                        {/* Drag handle */}
                        {isUpcoming && !isOnAir && <GripVertical className="w-3 h-3 text-gray-700 flex-shrink-0 mt-1" />}
                        <div className="w-14 h-9 bg-black rounded overflow-hidden flex-shrink-0 relative">
                          {isVideo ? <video src={getFullUrl(ad.url)} className="w-full h-full object-cover" muted preload="metadata" /> : <img src={getFullUrl(ad.url)} alt="" className="w-full h-full object-cover" />}
                          {isPlayingInBreak && <div className="absolute inset-0 bg-[#FF3B30]/30 flex items-center justify-center"><Volume2 className="w-3 h-3 text-white animate-pulse" /></div>}
                          {isPlayed && !isPlayingInBreak && <div className="absolute inset-0 bg-black/50 flex items-center justify-center"><span className="text-[8px] font-mono text-gray-400 uppercase">Played</span></div>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs truncate font-medium ${isPlayed ? 'text-gray-600' : 'text-white'}`}>{ad.filename}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <Badge className={`text-[9px] font-mono px-1 py-0 rounded ${ad.slotDuration === 30 ? 'bg-[#7C3AED]/10 text-[#7C3AED] border-[#7C3AED]/20' : 'bg-white/5 text-gray-400 border-white/10'}`}>
                              {ad.slotDuration || 15}s
                            </Badge>
                            {isVideo ? <FileVideo className="w-2.5 h-2.5 text-gray-600" /> : <Image className="w-2.5 h-2.5 text-gray-600" />}
                          </div>
                          {ad.isPlacement && <div className="flex items-center gap-1 mt-1"><DollarSign className="w-2.5 h-2.5 text-emerald-500" /><span className="text-[10px] text-emerald-400 font-mono font-bold">${(ad.slotPrice || 0).toFixed(2)}/slot</span><span className="text-[10px] text-gray-600 font-mono">{ad.slotsRemaining || '?'} left</span></div>}
                          {ad.pacingBlockReason && !isPlayed && (
                            <div data-testid={`pacing-block-${ad.id}`} className="flex items-center gap-1 mt-1">
                              <Ban className="w-2.5 h-2.5 text-yellow-500" />
                              <span className="text-[9px] text-yellow-500/80 font-mono uppercase tracking-wide">{ad.pacingBlockReason}</span>
                            </div>
                          )}
                          {isPlayingInBreak && <span className="text-[10px] font-mono text-[#FF3B30] uppercase tracking-wider mt-1 flex items-center gap-1"><div className="w-1.5 h-1.5 bg-[#FF3B30] rounded-full animate-pulse" /> On Air</span>}
                        </div>
                        {/* Remove button */}
                        {isUpcoming && !isOnAir && ad.placementId && (
                          <button onClick={(e) => { e.stopPropagation(); removeFromQueue(ad.placementId); }}
                            className="text-gray-700 hover:text-red-400 transition-colors flex-shrink-0 mt-1 opacity-0 group-hover:opacity-100"
                            title="Remove from queue">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    );
                  });
                  })()}
                </div>
              )}
            </div>
          </div>

          {/* CENTER: Monitor + Controls */}
          <div className="flex-1 flex flex-col items-center justify-center p-8 relative">
            {/* Ambient glow */}
            {isOnAir && <div className="absolute bottom-20 left-1/2 -translate-x-1/2 w-[500px] h-[300px] bg-[#FF3B30]/[0.04] rounded-full blur-[100px] pointer-events-none animate-pulse" />}

            {/* Preview Monitor */}
            <div className="w-full max-w-2xl mb-8">
              {isOnAir && breakStatus === 'playing' && currentAd ? (
                <div className="relative">
                  <div className="aspect-video bg-black rounded-lg overflow-hidden border border-[#FF3B30]/30 shadow-[0_0_30px_rgba(255,59,48,0.1)]">
                    {currentAd.mimeType?.startsWith('video/') ? (
                      <video src={getFullUrl(currentAd.url)} className="w-full h-full object-cover" muted preload="metadata" />
                    ) : (
                      <img src={getFullUrl(currentAd.url)} alt="" className="w-full h-full object-cover" />
                    )}
                    <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 1px, rgba(0,0,0,0.3) 1px, rgba(0,0,0,0.3) 2px)' }} />
                    {countdown && (
                      <div className="absolute inset-0 bg-black/90 flex items-center justify-center z-10">
                        <div className="flex items-center gap-8">
                          {[3, 2, 1].map(n => (
                            <span key={n} className={`font-heading font-black transition-all duration-500 ${
                              countdown === n ? 'text-[#FF3B30] text-9xl scale-110 opacity-100' :
                              countdown < n ? 'text-gray-800 text-6xl opacity-30' :
                              'text-[#FF3B30]/20 text-6xl opacity-50'
                            }`}>{n}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="absolute top-3 left-3 flex items-center gap-2 bg-[#FF3B30] px-2.5 py-1 rounded"><div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" /><span className="text-[10px] font-mono text-white uppercase tracking-widest font-bold">On Air</span></div>
                    <div className="absolute top-3 right-3 bg-black/60 px-2 py-1 rounded"><span className="text-[10px] font-mono text-white">{currentAdIndex + 1}/{activeBreak?.totalAds}</span></div>
                  </div>
                  <div className="flex items-center justify-between mt-3 px-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-white font-medium">{currentAd.filename}</span>
                      <Badge className="text-[9px] font-mono px-1 py-0">{currentAd.slotDuration || 15}s</Badge>
                    </div>
                    <span className="text-[10px] text-gray-500 font-mono">{currentAd.brandName}</span>
                  </div>
                  {/* Break progress */}
                  <div className="mt-3">
                    <Progress value={((currentAdIndex + 1) / (activeBreak?.totalAds || 1)) * 100} className="h-1 bg-white/5" />
                    <p className="text-[10px] text-gray-600 font-mono mt-1 text-center">Ad {currentAdIndex + 1} of {activeBreak?.totalAds}</p>
                  </div>
                </div>
              ) : isOnAir && breakStatus === 'holding' ? (
                <div className="relative">
                  <div className="aspect-video bg-[#0a0a0a] rounded-lg overflow-hidden border border-yellow-500/20 flex items-center justify-center">
                    {activeBreak?.holdingImageUrl ? (
                      <img src={getFullUrl(activeBreak.holdingImageUrl)} alt="Holding" className="w-full h-full object-contain" />
                    ) : (
                      <div className="text-center"><Monitor className="w-12 h-12 text-gray-700 mx-auto mb-3" /><p className="text-sm text-gray-500">Holding Screen</p><p className="text-xs text-gray-600 mt-1">Upload a holding image in Profile settings</p></div>
                    )}
                    <div className="absolute top-3 left-3 flex items-center gap-2 bg-yellow-500/80 px-2.5 py-1 rounded"><span className="text-[10px] font-mono text-black uppercase tracking-widest font-bold">Holding</span></div>
                  </div>
                  <p className="text-xs text-gray-500 text-center mt-3">All ads complete. Click "Back to Stream" when ready.</p>
                </div>
              ) : (
                /* IDLE STATE - show first ad preview or empty */
                <div className="relative">
                  <div className="aspect-video bg-black rounded-lg overflow-hidden border border-white/10 shadow-2xl shadow-black/50">
                    {approvedAds.length > 0 ? (
                      approvedAds[0].mimeType?.startsWith('video/') ?
                        <video src={getFullUrl(approvedAds[0].url)} className="w-full h-full object-cover opacity-60" muted preload="metadata" /> :
                        <img src={getFullUrl(approvedAds[0].url)} alt="" className="w-full h-full object-cover opacity-60" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Monitor className="w-10 h-10 text-gray-800" /></div>
                    )}
                    <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 1px, rgba(0,0,0,0.3) 1px, rgba(0,0,0,0.3) 2px)' }} />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <span className="text-xs font-mono text-gray-500 uppercase tracking-widest">Preview &middot; {approvedAds.length} ads queued</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* CONTROL BUTTONS */}
            <div className="flex flex-col items-center gap-4">
              {!isOnAir ? (
                /* IDLE: Show Cut to Commercial */
                <>
                  <div className="flex items-center gap-4">
                    <Tooltip><TooltipTrigger asChild>
                      <button data-testid="countdown-trigger-button" onClick={() => startBreak(true)} disabled={!canCutToCommercial || countdown !== null}
                        className={`w-20 h-16 rounded-lg flex items-center justify-center gap-1 transition-all ${canCutToCommercial && countdown === null ? 'bg-white/5 hover:bg-white/10 border border-white/10 cursor-pointer' : countdown !== null ? 'bg-[#FF3B30]/10 border border-[#FF3B30]/30' : 'bg-white/[0.02] border border-white/5 cursor-not-allowed'}`}>
                        {[3, 2, 1].map(n => (
                          <span key={n} className={`text-lg font-mono font-black transition-all duration-300 ${
                            countdown === n ? 'text-[#FF3B30] scale-125' :
                            countdown !== null && countdown < n ? 'text-gray-700' :
                            countdown !== null && countdown > n ? 'text-[#FF3B30]/30' :
                            'text-gray-500'
                          }`}>{n}</span>
                        ))}
                      </button>
                    </TooltipTrigger><TooltipContent className="bg-[#1E1E1E] border-white/10 text-white">3s countdown then start break</TooltipContent></Tooltip>

                    <button data-testid="cut-to-commercial-button" onClick={() => startBreak(false)} disabled={!canCutToCommercial}
                      className={`relative group transition-all duration-200 ${canCutToCommercial ? 'hover:scale-[1.02] active:scale-[0.97]' : ''}`}>
                      <div className={`absolute -inset-2 rounded-2xl transition-all duration-500 ${canCutToCommercial ? 'bg-[#FF3B30]/5 shadow-[0_0_30px_rgba(255,59,48,0.15)] group-hover:bg-[#FF3B30]/10 group-hover:shadow-[0_0_50px_rgba(255,59,48,0.3)]' : 'bg-transparent'}`} />
                      <div className={`relative w-44 h-44 rounded-xl flex flex-col items-center justify-center overflow-hidden ${canCutToCommercial ? 'bg-[#FF3B30] cursor-pointer' : 'bg-gray-800 cursor-not-allowed'}`}
                        style={{ boxShadow: canCutToCommercial ? 'inset 0 -4px 0 rgba(0,0,0,0.3), inset 0 2px 0 rgba(255,255,255,0.1)' : 'none' }}>
                        <div className="absolute inset-0 bg-gradient-to-b from-white/[0.08] to-transparent pointer-events-none" />
                        <Play className={`w-8 h-8 mb-2 ${canCutToCommercial ? 'text-white' : 'text-gray-600'}`} />
                        <span className={`text-[10px] font-mono uppercase tracking-[0.15em] ${canCutToCommercial ? 'text-white/70' : 'text-gray-600'}`}>Cut To</span>
                        <span className={`text-sm font-heading font-black uppercase tracking-wider ${canCutToCommercial ? 'text-white' : 'text-gray-600'}`}>Commercial</span>
                      </div>
                    </button>

                    <div className="w-16 h-16" /> {/* Spacer for symmetry */}
                  </div>
                  {canCutToCommercial && (
                    <p className="text-[10px] font-mono text-gray-600 uppercase tracking-wider">
                      {approvedAds.length > 0 ? `${approvedAds.length} ads will play back-to-back` : 'Holding screen will display'}
                    </p>
                  )}
                </>
              ) : (
                /* ON AIR: Show Back to Stream */
                <>
                  <button data-testid="back-to-stream-button" onClick={backToStream}
                    className="relative group transition-all duration-200 hover:scale-[1.02] active:scale-[0.97]">
                    <div className="absolute -inset-2 rounded-2xl bg-emerald-500/5 shadow-[0_0_30px_rgba(16,185,129,0.15)] group-hover:bg-emerald-500/10 group-hover:shadow-[0_0_50px_rgba(16,185,129,0.3)] transition-all duration-500" />
                    <div className="relative w-44 h-44 rounded-xl flex flex-col items-center justify-center overflow-hidden bg-emerald-600 cursor-pointer"
                      style={{ boxShadow: 'inset 0 -4px 0 rgba(0,0,0,0.3), inset 0 2px 0 rgba(255,255,255,0.1)' }}>
                      <div className="absolute inset-0 bg-gradient-to-b from-white/[0.08] to-transparent pointer-events-none" />
                      <ArrowLeftCircle className="w-8 h-8 text-white mb-2" />
                      <span className="text-[10px] font-mono text-white/70 uppercase tracking-[0.15em]">Back To</span>
                      <span className="text-sm font-heading font-black text-white uppercase tracking-wider">Stream</span>
                    </div>
                  </button>
                  {breakStatus === 'playing' && (
                    <p className="text-[10px] font-mono text-yellow-400 uppercase tracking-wider">Current ad will finish, then return</p>
                  )}
                  {breakStatus === 'holding' && (
                    <p className="text-[10px] font-mono text-emerald-400 uppercase tracking-wider">Holding screen active &middot; Click to return</p>
                  )}
                  {breakStatus === 'ending' && (
                    <div className="flex flex-col items-center gap-2">
                      <p className="text-[10px] font-mono text-yellow-400 uppercase tracking-wider animate-pulse">Ad finishing — returning to stream...</p>
                      <button data-testid="force-end-break" onClick={() => {
                        axios.post(`${API}/ads/back-to-stream`, {}, { headers }).catch(() => {});
                        setActiveBreak(null); setBreakStatus(null); fetchAds(); toast.success('Break ended.');
                      }} className="text-[10px] font-mono text-gray-600 hover:text-red-400 uppercase tracking-wider transition-colors">
                        End Now
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* RIGHT: Break Status + Session */}
          <div className="w-72 border-l border-white/[0.06] bg-[#0a0a0a] flex flex-col flex-shrink-0">
            <div className="px-4 py-3 border-b border-white/[0.06]">
              <span className="text-xs font-mono text-gray-500 uppercase tracking-widest">Session</span>
              <div className="grid grid-cols-3 gap-2 mt-3">
                <div className="text-center"><p className="text-lg font-bold text-white font-mono">{sessionTriggers}</p><p className="text-[9px] text-gray-600 font-mono uppercase">Ads</p></div>
                <div className="text-center"><p className="text-lg font-bold text-emerald-400 font-mono">${sessionEarnings.toFixed(2)}</p><p className="text-[9px] text-gray-600 font-mono uppercase">Earned</p></div>
                <div className="text-center"><p className="text-lg font-bold text-white font-mono">{formatTime(sessionTime).slice(0,5)}</p><p className="text-[9px] text-gray-600 font-mono uppercase">Uptime</p></div>
              </div>
            </div>

            {/* Break Scheduler */}
            <div data-testid="break-scheduler-panel" className="px-4 py-3 border-b border-white/[0.06]">
              <div className="flex items-center gap-2 mb-3">
                <AlarmClock className="w-3.5 h-3.5 text-[#FF3B30]" />
                <span className="text-xs font-mono text-gray-500 uppercase tracking-widest">Break Scheduler</span>
              </div>
              {cadence > 0 ? (
                <div className="mb-3 rounded-lg border border-white/[0.07] bg-black/40 px-3 py-3">
                  <p className="text-[9px] font-mono text-gray-600 uppercase tracking-widest">{nextIn !== null && nextIn <= 0 ? 'Break overdue' : 'Next break in'}</p>
                  <p data-testid="scheduler-countdown" className={`font-mono font-black tabular-nums leading-none mt-1 ${nextIn !== null && nextIn <= 60 ? 'text-[#FF3B30]' : 'text-white'}`} style={{ fontSize: '34px' }}>
                    {nextIn === null ? '--:--' : nextIn > 0 ? formatTime(nextIn).slice(3) : '00:00'}
                  </p>
                  <div className="mt-2"><Progress value={nextIn === null ? 0 : Math.min(100, Math.max(0, (1 - nextIn / (cadence * 60)) * 100))} className="h-1 bg-white/5" /></div>
                  <div className="flex items-center gap-2 mt-2">
                    <button data-testid="snooze-break" onClick={() => snooze(5)} className="text-[10px] font-mono text-gray-500 hover:text-white uppercase tracking-wider">Snooze 5m</button>
                    <span className="text-gray-700">·</span>
                    <button data-testid="reset-break-timer" onClick={() => { setLastBreakAt(Date.now()); nudgedRef.current = false; }} className="text-[10px] font-mono text-gray-500 hover:text-white uppercase tracking-wider">Reset</button>
                  </div>
                </div>
              ) : (
                <p className="text-[10px] text-gray-600 font-mono mb-3">Off — you're triggering every break by hand.</p>
              )}
              <label className="text-[10px] font-mono text-gray-600 uppercase tracking-widest block mb-1">Cadence</label>
              <Select value={String(cadence)} onValueChange={v => { const val = parseInt(v); setCadence(val); setLastBreakAt(Date.now()); nudgedRef.current = false; saveConsoleSettings({ breakCadenceMinutes: val }); }}>
                <SelectTrigger data-testid="break-cadence-select" className="bg-black/40 border-white/10 text-white h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  <SelectItem value="0" className="text-gray-300 text-xs">Off — manual only</SelectItem>
                  {[10, 15, 20, 30, 45, 60].map(m => <SelectItem key={m} value={String(m)} className="text-gray-300 text-xs">Every {m} min</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex items-center justify-between mt-3">
                <div>
                  <p className="text-[10px] font-mono text-gray-400 uppercase tracking-widest">Auto-fire</p>
                  <p className="text-[9px] text-gray-600 font-mono">Runs a 3-2-1 then cuts for you</p>
                </div>
                <Switch data-testid="auto-fire-switch" checked={autoFire} disabled={!cadence}
                  onCheckedChange={v => { setAutoFire(v); saveConsoleSettings({ autoFireBreaks: v }); }} />
              </div>
            </div>

            {/* Active Break Info */}
            {isOnAir && activeBreak && (
              <div className="px-4 py-3 border-b border-white/[0.06] bg-[#FF3B30]/[0.02]">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 bg-[#FF3B30] rounded-full animate-pulse" />
                  <span className="text-xs font-mono text-[#FF3B30] uppercase tracking-widest font-bold">Break Active</span>
                </div>
                <div className="space-y-1.5">
                  {activeBreak.ads?.map((ad, i) => (
                    <div key={i} className={`flex items-center gap-2 px-2 py-1 rounded text-[10px] font-mono ${
                      i < currentAdIndex ? 'text-gray-600 line-through' :
                      i === currentAdIndex && breakStatus === 'playing' ? 'text-[#FF3B30] bg-[#FF3B30]/5 font-bold' :
                      'text-gray-500'
                    }`}>
                      <span className="w-4 text-right">{i + 1}.</span>
                      <span className="truncate flex-1">{ad.filename}</span>
                      <span>{ad.slotDuration || 15}s</span>
                      {i === currentAdIndex && breakStatus === 'playing' && <Volume2 className="w-3 h-3 animate-pulse" />}
                      {i < currentAdIndex && <span className="text-emerald-500">done</span>}
                    </div>
                  ))}
                </div>
                {activeBreak.totalEarnings > 0 && (
                  <div className="mt-3 pt-2 border-t border-white/5 flex items-center justify-between">
                    <span className="text-[10px] font-mono text-gray-500">Break earnings</span>
                    <span className="text-xs font-mono text-emerald-400 font-bold">${activeBreak.totalEarnings.toFixed(4)}</span>
                  </div>
                )}
              </div>
            )}

            {/* Overlay Ads Panel */}
            <div className="flex-1 px-4 py-4 overflow-y-auto">
              <div className="flex items-center gap-2 mb-3">
                <Layers className="w-3.5 h-3.5 text-[#7C3AED]" />
                <span className="text-xs font-mono text-gray-500 uppercase tracking-widest">Overlay Ads</span>
              </div>

              {/* Overlay Config */}
              <div className="space-y-3 mb-4">
                <div>
                  <label className="text-[10px] font-mono text-gray-600 uppercase tracking-widest block mb-1">Auto-Run Interval</label>
                  <Select value={String(overlayInterval)} onValueChange={v => { const val = parseInt(v); setOverlayInterval(val); saveOverlayConfig(val, overlayDuration); }}>
                    <SelectTrigger data-testid="overlay-interval-select" className="bg-black/40 border-white/10 text-white h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/10">
                      <SelectItem value="0" className="text-gray-300 text-xs">Manual Only</SelectItem>
                      <SelectItem value="5" className="text-gray-300 text-xs">Every 5 min</SelectItem>
                      <SelectItem value="10" className="text-gray-300 text-xs">Every 10 min</SelectItem>
                      <SelectItem value="20" className="text-gray-300 text-xs">Every 20 min</SelectItem>
                      <SelectItem value="30" className="text-gray-300 text-xs">Every 30 min</SelectItem>
                      <SelectItem value="60" className="text-gray-300 text-xs">Every hour</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] font-mono text-gray-600 uppercase tracking-widest block mb-1">Display Duration</label>
                  <Select value={String(overlayDuration)} onValueChange={v => { const val = parseInt(v); setOverlayDuration(val); saveOverlayConfig(overlayInterval, val); }}>
                    <SelectTrigger data-testid="overlay-duration-select" className="bg-black/40 border-white/10 text-white h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/10">
                      <SelectItem value="5" className="text-gray-300 text-xs">5 seconds</SelectItem>
                      <SelectItem value="10" className="text-gray-300 text-xs">10 seconds</SelectItem>
                      <SelectItem value="15" className="text-gray-300 text-xs">15 seconds</SelectItem>
                      <SelectItem value="20" className="text-gray-300 text-xs">20 seconds</SelectItem>
                      <SelectItem value="30" className="text-gray-300 text-xs">30 seconds</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Overlay position */}
              <div className="mb-4">
                <label className="text-[10px] font-mono text-gray-600 uppercase tracking-widest block mb-1">Overlay Position</label>
                <Select value={overlayPosition} onValueChange={v => { setOverlayPosition(v); saveConsoleSettings({ overlayPosition: v }); }}>
                  <SelectTrigger data-testid="overlay-position-select" className="bg-black/40 border-white/10 text-white h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10">
                    <SelectItem value="bottom-right" className="text-gray-300 text-xs">Corner badge</SelectItem>
                    <SelectItem value="lower-third" className="text-gray-300 text-xs">Lower third banner</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Look & feel */}
              <div className="mb-4 pt-3 border-t border-white/[0.06] space-y-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-[#FF3B30]" />
                  <span className="text-xs font-mono text-gray-500 uppercase tracking-widest">Break Look</span>
                </div>
                <div>
                  <label className="text-[10px] font-mono text-gray-600 uppercase tracking-widest block mb-1">Transition</label>
                  <Select value={transitionStyle} onValueChange={v => { setTransitionStyle(v); saveConsoleSettings({ transitionStyle: v }); }}>
                    <SelectTrigger data-testid="transition-style-select" className="bg-black/40 border-white/10 text-white h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/10">
                      <SelectItem value="fade" className="text-gray-300 text-xs">Fade</SelectItem>
                      <SelectItem value="wipe" className="text-gray-300 text-xs">Wipe</SelectItem>
                      <SelectItem value="glitch" className="text-gray-300 text-xs">Glitch</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] font-mono text-gray-600 uppercase tracking-widest block mb-1">Holding "Back In" Timer</label>
                  <Select value={String(holdingCountdownSec)} onValueChange={v => { const val = parseInt(v); setHoldingCountdownSec(val); saveConsoleSettings({ holdingCountdownSeconds: val }); }}>
                    <SelectTrigger data-testid="holding-countdown-select" className="bg-black/40 border-white/10 text-white h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/10">
                      <SelectItem value="0" className="text-gray-300 text-xs">Hidden</SelectItem>
                      {[30, 45, 60, 90, 120, 180].map(s => <SelectItem key={s} value={String(s)} className="text-gray-300 text-xs">{s < 60 ? `${s} seconds` : `${s / 60} min`}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Overlay Ad List */}
              {overlayAds.length === 0 ? (
                <div className="text-center py-4"><Layers className="w-6 h-6 mx-auto mb-1 text-gray-800" /><p className="text-[10px] text-gray-600">No overlay ads</p></div>
              ) : (
                <div className="space-y-1.5 mb-3">
                  {overlayAds.slice(0, 5).map(ad => (
                    <button key={ad.id} data-testid={`overlay-ad-${ad.id}`} onClick={() => setSelectedOverlay(ad)}
                      className={`w-full flex items-center gap-2 p-2 rounded text-left transition-all ${selectedOverlay?.id === ad.id ? 'bg-[#7C3AED]/10 border border-[#7C3AED]/20' : 'hover:bg-white/[0.02] border border-transparent'}`}>
                      <div className="w-10 h-7 bg-black rounded overflow-hidden flex-shrink-0">
                        <img src={getFullUrl(ad.url)} alt="" className="w-full h-full object-cover" />
                      </div>
                      <span className="text-[10px] text-gray-300 truncate flex-1">{ad.filename}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Manual Trigger */}
              <Button data-testid="trigger-overlay-button" onClick={() => triggerOverlay()} disabled={!selectedOverlay || overlayActive}
                className={`w-full text-xs font-bold uppercase tracking-wider h-9 ${overlayActive ? 'bg-[#7C3AED]/50' : 'bg-[#7C3AED] hover:bg-[#6D28D9]'} text-white`}>
                <Layers className="w-3.5 h-3.5 mr-1.5" /> {overlayActive ? 'Overlay Active...' : 'Show Overlay'}
              </Button>

              {overlayInterval > 0 && (
                <div className="mt-2 flex items-center gap-1.5 justify-center">
                  <Timer className="w-3 h-3 text-[#7C3AED]" />
                  <span className="text-[10px] font-mono text-[#7C3AED]">Auto every {overlayInterval}m &middot; {overlayDuration}s display</span>
                </div>
              )}
            </div>

            <div className="px-4 py-3 border-t border-white/[0.06] bg-[#050505]">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono text-gray-500 uppercase">Earnings</span>
                <button onClick={() => navigate('/streamer/earnings')} className="text-[10px] text-gray-500 hover:text-white font-mono">View →</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
